/**
 * Runtime env resolution.
 *
 * Values are resolved in this order:
 *   1. import.meta.env.VITE_* — inlined at build time by Vite from .env files
 *   2. window.__REACHOUT_AI_DEFAULTS__ — injected via /runtime-config.js at runtime,
 *      so CloudFront can override endpoints per environment without a rebuild
 *
 * The window path is authoritative in production: the same JS bundle is served
 * across staging/staging2a/production with per-environment runtime-config.js
 * files generated at deploy time.
 */
export const resolveRuntimeEnv = (key: string): string | undefined => {
	const viteKey = key.startsWith('VITE_') ? key : `VITE_${key}`;

	// 1. Vite inline env (build time) — only present if import.meta.env exists
	const meta = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
	if (meta) {
		const value = meta[viteKey];
		if (typeof value === 'string' && value.length > 0) return value;
	}

	// 2. window runtime override (deploy time) — accepts both VITE_* and legacy NEXT_PUBLIC_* keys
	if (typeof window !== 'undefined') {
		const defaults = (
			window as unknown as {
				__REACHOUT_AI_DEFAULTS__?: Record<string, string | undefined>;
			}
		).__REACHOUT_AI_DEFAULTS__;
		if (defaults) {
			if (defaults[viteKey] !== undefined) return defaults[viteKey];
			const legacyKey = viteKey.replace(/^VITE_/, 'NEXT_PUBLIC_');
			if (defaults[legacyKey] !== undefined) return defaults[legacyKey];
		}
	}

	return undefined;
};

export function getTransportConfig() {
	return {
		// ReachOut AI (AppSync Events)
		apiEndpoint: resolveRuntimeEnv('APPSYNC_EVENTS_ENDPOINT') ?? '',
		realEndpoint: resolveRuntimeEnv('APPSYNC_REAL_ENDPOINT') ?? '',
		identityPoolId: resolveRuntimeEnv('COGNITO_IDENTITY_POOL_ID') ?? '',
		// The Events API has no API-key auth; students always connect over IAM.
		authMode: 'iam' as const,
		region: resolveRuntimeEnv('AWS_REGION') ?? 'us-west-2',
		streaming: false,

		// Classic Chat (Amazon Connect)
		connectApiEndpoint: resolveRuntimeEnv('CONNECT_API_ENDPOINT') ?? '',
		connectInstanceId: resolveRuntimeEnv('CONNECT_INSTANCE_ID') ?? '',
		contactFlowId: resolveRuntimeEnv('CONTACT_FLOW_ID') ?? '',
		connectCode: resolveRuntimeEnv('CONNECT_CODE') ?? '',

		// Shared
		districtId: resolveRuntimeEnv('DISTRICT_ID') ?? '',
		districtName: resolveRuntimeEnv('DISTRICT_NAME') ?? '',
		inviteCode: resolveRuntimeEnv('INVITE_CODE') ?? '',

		// Chat-handler HTTP base URL — used for the /v1/endpoint/{code}/status
		// kill-switch probe and any future synchronous calls that don't fit the
		// AppSync Events channel model. Empty in dev → status guard becomes a
		// no-op (backend joinSession check still enforces the kill switch).
		chatHttpEndpoint: resolveRuntimeEnv('CHAT_HTTP_ENDPOINT') ?? '',

		// AWS WAF JS SDK base URL for the AppSync Events WebACL — fetched at
		// deploy time from the SSM parameter the nomi backend stack publishes.
		// Empty in dev → BotChallenge feature is a no-op.
		wafIntegrationUrl: resolveRuntimeEnv('WAF_INTEGRATION_URL') ?? '',

		// AWS WAF CAPTCHA API key — passed to AwsWafCaptcha.renderCaptcha so
		// WAF accepts the puzzle solve from this client domain. Auto-generated
		// by the nomi CDK stack and exported via SSM. Both this and
		// `wafIntegrationUrl` must be present for the inline puzzle to render.
		wafCaptchaApiKey: resolveRuntimeEnv('WAF_CAPTCHA_API_KEY') ?? '',
	};
}
